/** Catalog snapshot on an ERP sync audit event. */
export type ErpSyncCatalogAmounts = {
  retail?: number | null;
  map?: number | null;
  msrp?: number | null;
  salePrice?: number | null;
  instantRebate?: number | null;
  basePrice?: number | null;
};

export type ErpSyncPushedAmounts = {
  price?: number | null;
  compareAtPrice?: number | null;
};

const CENTS_EPS = 0.02;

function finitePositive(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function amountsClose(a: number, b: number): boolean {
  return Math.abs(a - b) < CENTS_EPS;
}

/**
 * Instant rebate to show on "Catalog at sync". Leftover `pricing.instantRebate`
 * stays on the product after a window ends (Panasonic leftover-IR workaround),
 * so dumping it on every push reads as Motzu sending a $200 rebate when the
 * advertised price was only $50 off MAP.
 *
 * Show IR only when the advertised price Motzu sent is MAP − IR.
 */
export function instantRebateAppliedToPush(
  catalog?: ErpSyncCatalogAmounts | null,
  pushed?: ErpSyncPushedAmounts | null,
): number | null {
  const ir = finitePositive(catalog?.instantRebate);
  const map = finitePositive(catalog?.map);
  if (ir == null || map == null) return null;
  const afterIR = Math.round((map - ir) * 100) / 100;
  if (!(afterIR > 0) || afterIR >= map - CENTS_EPS) return null;
  const advertised = finitePositive(pushed?.price);
  if (advertised == null || !amountsClose(advertised, afterIR)) return null;
  return ir;
}

export function formatErpSyncCatalogLine(
  catalog: ErpSyncCatalogAmounts | null | undefined,
  pushed: ErpSyncPushedAmounts | null | undefined,
  money: (value: number | null | undefined) => string,
): string {
  const ir = instantRebateAppliedToPush(catalog, pushed);
  return [
    catalog?.map != null ? `MAP ${money(catalog.map)}` : null,
    catalog?.salePrice != null ? `sale ${money(catalog.salePrice)}` : null,
    catalog?.retail != null ? `retail ${money(catalog.retail)}` : null,
    catalog?.msrp != null ? `MSRP ${money(catalog.msrp)}` : null,
    ir != null ? `IR ${money(ir)}` : null,
  ]
    .filter(Boolean)
    .join(' · ') || '—';
}

/** One row of an event's "What changed" list (flag `sync_audit_field_history`). */
export type ErpSyncFieldChange = { field: string; before: unknown; after: unknown };

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  productType: 'Product type',
  category: 'Shopify category',
  tags: 'Tags',
  images: 'Images',
  weight: 'Weight',
  status: 'Status',
  // Lightspeed-only keys.
  shortDescription: 'Short description',
  note: 'Note',
  categoryId: 'Category',
  price: 'Price',
  compareAtPrice: 'Compare-at price',
};

export function formatFieldChangeName(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (field.startsWith('customField:')) return `Custom field ${field.slice('customField:'.length)}`;
  return field.startsWith('metafield:') ? `Metafield ${field.slice('metafield:'.length)}` : field;
}

/** A stored before/after value as short text. A description arrives as `{ length, sha1 }`, never the HTML. */
export function formatFieldChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map((v) => formatFieldChangeValue(v)).join(', ');
  if (typeof value === 'object') {
    const row = value as { length?: unknown; sha1?: unknown };
    if (typeof row.length === 'number' && typeof row.sha1 === 'string') return `${row.length} characters`;
    return JSON.stringify(value);
  }
  return String(value);
}

/** Mirrors `src/types/copyProvenance.ts` (flag `copy_provenance`). */
export type CopySource =
  | 'store_override'
  | 'catalog_manufacturer'
  | 'catalog_scrape'
  | 'motzu_ai'
  | 'motzu_template'
  | 'unknown';

export type CopyFieldProvenance = {
  source: CopySource;
  sourceRef?: string;
  at?: string;
  adapted?: boolean;
};

export type CopyProvenance = {
  title?: CopyFieldProvenance;
  description?: CopyFieldProvenance;
};

const COPY_SOURCE_LABELS: Record<CopySource, string> = {
  store_override: 'Your own edit',
  catalog_manufacturer: 'Manufacturer data',
  catalog_scrape: 'Manufacturer product page',
  motzu_ai: 'Motzu AI',
  motzu_template: 'Motzu template',
  unknown: 'Unknown source',
};

function hostOf(ref: string | undefined): string | null {
  if (!ref) return null;
  try {
    return new URL(ref).hostname.replace(/^www\./, '');
  } catch {
    return ref;
  }
}

function shortDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Chip text and the line that follows it, e.g. "Manufacturer product page" / "sony.com, scraped 12 Sep". */
export function describeCopySource(field: CopyFieldProvenance): { label: string; detail: string | null } {
  const host = hostOf(field.sourceRef);
  const date = shortDate(field.at);
  let detail: string | null = null;
  if (field.source === 'catalog_scrape') {
    detail = [host, date ? `scraped ${date}` : null].filter(Boolean).join(', ') || null;
  } else if (field.source === 'catalog_manufacturer') {
    detail = [host, date ? `imported ${date}` : null].filter(Boolean).join(', ') || null;
  } else if (field.source === 'store_override') {
    detail = date ? `edited ${date}` : null;
  }
  return { label: COPY_SOURCE_LABELS[field.source] ?? COPY_SOURCE_LABELS.unknown, detail };
}
